import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Users from "./pages/Users";
import TierConfig from "./pages/TierConfig";
import SecurityDashboard from "./pages/SecurityDashboard";

// App Pages
import AppraisalList from "./pages/apps/AppraisalList";
import AssetAppraisalApp from "./pages/apps/AssetAppraisalApp";
import BankAnalysisList from "./pages/apps/BankAnalysisList";
import BankAnalysisApp from "./pages/apps/BankAnalysisApp";
import CreditReportAnalysisList from "./pages/apps/CreditReportAnalysisList";
import CreditReportAnalysisApp from "./pages/apps/CreditReportAnalysisApp";
import CreditEvaluationList from "./pages/apps/CreditEvaluationList";
import CreditEvaluationApp from "./pages/apps/CreditEvaluationApp";
import PricingList from "./pages/apps/PricingList";
import PricingApp from "./pages/apps/PricingApp";
import SummaryList from "./pages/apps/SummaryList";
import SummaryApp from "./pages/apps/SummaryApp";

// Training Academy Pages
import TrainingDashboard from "./pages/training/TrainingDashboard";
import TrainingModule from "./pages/training/TrainingModule";
import TrainingLesson from "./pages/training/TrainingLesson";
import TrainingQuiz from "./pages/training/TrainingQuiz";
import TrainingContentEditor from "./pages/training/TrainingContentEditor";
import TrainingAdminDashboard from "./pages/training/TrainingAdminDashboard";
import TrainingSandbox from "./pages/training/TrainingSandbox";
import SandboxQuiz from "./pages/training/SandboxQuiz";
import FinalExam from "./pages/training/FinalExam";
import InteractiveSimulation from "./pages/training/InteractiveSimulation";
import AssessmentBuilder from "./pages/training/AssessmentBuilder";
import TakeAssessment from "./pages/training/TakeAssessment";

// Deals Pipeline Pages
import DealsPipeline from "./pages/deals/DealsPipeline";
import DealDetail from "./pages/deals/DealDetail";
import ActivationFlow from "./pages/deals/ActivationFlow";
import CustomerPortal from "./pages/deals/CustomerPortal";

import "@/App.css";

const PrivateRoute = ({ children, adminOnly = false, creditOnly = false }) => {
  const { user, loading, isAdmin, isTrainee, canAccessCredit } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Trainees without credit access can ONLY use Training Academy routes.
  // Any direct navigation to /dashboard, /clients, /apps/* etc. bounces them to /training.
  if (isTrainee() && !canAccessCredit()) {
    return <Navigate to="/training" replace />;
  }

  // Redirect trainees away from credit modules
  if (creditOnly && isTrainee() && !canAccessCredit()) {
    return <Navigate to="/training" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const TrainingRoute = ({ children }) => {
  const { user, loading, canAccessTraining } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessTraining()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Public Routes - No Login Required */}
      <Route path="/activate/:token" element={<ActivationFlow />} />
      <Route path="/portal/:token" element={<CustomerPortal />} />
      
      {/* Dashboard */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <Dashboard />
        </PrivateRoute>
      } />
      
      {/* Clients */}
      <Route path="/clients" element={
        <PrivateRoute>
          <Clients />
        </PrivateRoute>
      } />
      <Route path="/clients/new" element={
        <PrivateRoute>
          <Clients />
        </PrivateRoute>
      } />
      <Route path="/clients/:id" element={
        <PrivateRoute>
          <ClientDetail />
        </PrivateRoute>
      } />
      
      {/* Asset Appraisal App */}
      <Route path="/apps/appraisal" element={
        <PrivateRoute>
          <AppraisalList />
        </PrivateRoute>
      } />
      <Route path="/apps/appraisal/new" element={
        <PrivateRoute>
          <AssetAppraisalApp key="new-appraisal" />
        </PrivateRoute>
      } />
      <Route path="/apps/appraisal/:id" element={
        <PrivateRoute>
          <AssetAppraisalApp key="edit-appraisal" />
        </PrivateRoute>
      } />
      
      {/* Bank Analysis App */}
      <Route path="/apps/bank-analysis" element={
        <PrivateRoute>
          <BankAnalysisList />
        </PrivateRoute>
      } />
      <Route path="/apps/bank-analysis/new" element={
        <PrivateRoute>
          <BankAnalysisApp />
        </PrivateRoute>
      } />
      <Route path="/apps/bank-analysis/:id" element={
        <PrivateRoute>
          <BankAnalysisApp />
        </PrivateRoute>
      } />
      
      {/* Credit Report Analysis App (Soft Pull) */}
      <Route path="/apps/credit-report" element={
        <PrivateRoute>
          <CreditReportAnalysisList />
        </PrivateRoute>
      } />
      <Route path="/apps/credit-report/:id" element={
        <PrivateRoute>
          <CreditReportAnalysisApp />
        </PrivateRoute>
      } />
      
      {/* Credit Evaluation App */}
      <Route path="/apps/credit-evaluation" element={
        <PrivateRoute>
          <CreditEvaluationList />
        </PrivateRoute>
      } />
      <Route path="/apps/credit-evaluation/new" element={
        <PrivateRoute>
          <CreditEvaluationApp />
        </PrivateRoute>
      } />
      <Route path="/apps/credit-evaluation/:id" element={
        <PrivateRoute>
          <CreditEvaluationApp />
        </PrivateRoute>
      } />
      
      {/* Pricing & Structuring App */}
      <Route path="/apps/pricing" element={
        <PrivateRoute>
          <PricingList />
        </PrivateRoute>
      } />
      <Route path="/apps/pricing/new" element={
        <PrivateRoute>
          <PricingApp />
        </PrivateRoute>
      } />
      <Route path="/apps/pricing/:id" element={
        <PrivateRoute>
          <PricingApp />
        </PrivateRoute>
      } />
      
      {/* Decision & Summary App */}
      <Route path="/apps/summary" element={
        <PrivateRoute>
          <SummaryList />
        </PrivateRoute>
      } />
      <Route path="/apps/summary/new" element={
        <PrivateRoute>
          <SummaryApp />
        </PrivateRoute>
      } />
      <Route path="/apps/summary/:id" element={
        <PrivateRoute>
          <SummaryApp />
        </PrivateRoute>
      } />
      
      {/* Deals Pipeline */}
      <Route path="/deals" element={
        <PrivateRoute>
          <DealsPipeline />
        </PrivateRoute>
      } />
      <Route path="/deals/:dealId" element={
        <PrivateRoute>
          <DealDetail />
        </PrivateRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/users" element={
        <PrivateRoute adminOnly>
          <Users />
        </PrivateRoute>
      } />
      <Route path="/admin/tiers" element={
        <PrivateRoute adminOnly>
          <TierConfig />
        </PrivateRoute>
      } />
      <Route path="/admin/security" element={
        <PrivateRoute adminOnly>
          <SecurityDashboard />
        </PrivateRoute>
      } />
      
      {/* Training Academy Routes */}
      <Route path="/training" element={
        <TrainingRoute>
          <TrainingDashboard />
        </TrainingRoute>
      } />
      <Route path="/training/module/:moduleId" element={
        <TrainingRoute>
          <TrainingModule />
        </TrainingRoute>
      } />
      <Route path="/training/lesson/:lessonId" element={
        <TrainingRoute>
          <TrainingLesson />
        </TrainingRoute>
      } />
      <Route path="/training/quiz/:quizId" element={
        <TrainingRoute>
          <TrainingQuiz />
        </TrainingRoute>
      } />
      <Route path="/training/admin/editor" element={
        <PrivateRoute adminOnly>
          <TrainingContentEditor />
        </PrivateRoute>
      } />
      <Route path="/training/admin/dashboard" element={
        <PrivateRoute adminOnly>
          <TrainingAdminDashboard />
        </PrivateRoute>
      } />
      <Route path="/training/admin/assessments" element={
        <PrivateRoute adminOnly>
          <AssessmentBuilder />
        </PrivateRoute>
      } />
      <Route path="/training/admin/take-assessment/:type/:id" element={
        <PrivateRoute adminOnly>
          <TakeAssessment />
        </PrivateRoute>
      } />
      <Route path="/training/admin/take-assessment/:type" element={
        <PrivateRoute adminOnly>
          <TakeAssessment />
        </PrivateRoute>
      } />
      <Route path="/training/sandbox" element={
        <TrainingRoute>
          <TrainingSandbox />
        </TrainingRoute>
      } />
      <Route path="/training/sandbox/quiz" element={
        <TrainingRoute>
          <SandboxQuiz />
        </TrainingRoute>
      } />
      <Route path="/training/final-exam" element={
        <TrainingRoute>
          <FinalExam />
        </TrainingRoute>
      } />
      <Route path="/training/simulation/:simulationId" element={
        <TrainingRoute>
          <InteractiveSimulation />
        </TrainingRoute>
      } />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
